import React, {useEffect, useMemo, useRef, useState} from "react";
import {connect} from "react-redux";
import {Dispatch} from "redux";
import {v4 as uuidv4} from "uuid";
import {usePrescriptionItemIhioCheck} from "../../../../../../../hook/request/visit/prescription";
import Modal from "../../../../../general/modal";
import {
    isHideModalDataSelector,
    isShowModalDataSelector,
    modalDataSelector,
    modalNameSelector
} from "../../../../../../../store/selector/general";
import {general} from "../../../../../../../store/action";
import {TIhioDataAndPrescriptionMedicationItemFields} from "../../../../../../../types/prescription";
import TextAreaField from "../../../../../general/form/textAreaField";
import NumberField from "../../../../../general/form/numberField";
import {SubmitHandler, useForm} from "react-hook-form";
import BulkItemCardItem from "./bulkItemCardItem";
import {AsyncSelectField} from "../../../../../general/form/selectField";
import {asyncSelectLoadOptions} from "../../../../../../../utils/asyncSelectLoadOptions";
import {useGetMedicationItems} from "../../../../../../../hook/request/medication";
import {useMySearchParams} from "../../../../../../../hook/useMySearchParams";
import Icon from "../../../../../general/icon";

type ModalInfoProps = {
    UUID?: string,
    item?: any,
    insurance?: number | null
    visitId?: number;
    index?: number;
    handleMedicationModalSubmit?: any;
}

type AddPrescriptionBulkItemModalProps = {
    modalData: any;
    modalName: string;
    isShowModalData: boolean;
    handleHideModal: any;
    handleResetModal: any;
}

type TFilterValues = {
    pageNumber?: string;
    perPage?: string;
    search?: string;
    bulk?: boolean;
}


const AddPrescriptionBulkItemModal = ({
    modalData,
    modalName,
    isShowModalData,
    handleHideModal,
    handleResetModal,
}: AddPrescriptionBulkItemModalProps) => {

    const [showState, setShowState] = useState(false);
    const [modalInfo, setModalInfo] = useState<ModalInfoProps>({});
    const [bulkItemFieldValues, setBulkItemFieldValues] = useState<TIhioDataAndPrescriptionMedicationItemFields>({});
    const [customMedicationItem, setCustomMedicationItem] = useState<TIhioDataAndPrescriptionMedicationItemFields>({});
    const [currentBulkId, setCurrentBulkId] = useState<number>(1)

    const { getQueryParams } = useMySearchParams();
    const [bulkItemFilterValues, setBulkItemFilterValues] = useState<TFilterValues>({
        pageNumber: "0",
        perPage: "30",
        search: "",
        bulk: true
    });
    const [bulkItemsList, setBulkItemsList] = useState<any>([]);

    const callbackRef = useRef<any>(null);

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm<TIhioDataAndPrescriptionMedicationItemFields>({
        defaultValues: useMemo(() => bulkItemFieldValues, [bulkItemFieldValues]),
    });

    const prescriptionItemIhioCheckRequest = usePrescriptionItemIhioCheck({
        onSuccess: () => {
        }
    });
    const getPrescriptionMedicationItemListRequest = useGetMedicationItems(
        getQueryParams(bulkItemFilterValues),modalName === 'addPrescriptionBulkItemModal');


    useEffect(() => {
        if(
            isShowModalData &&
            modalName === 'addPrescriptionBulkItemModal'
        ){
            setShowState(true);
            setModalInfo(modalData);
            handleResetModal();
        }
    }, [modalData, modalName])

    useEffect(() => {
        if(modalInfo?.item && showState) {
            setCustomMedicationItem(modalInfo?.item)
        }
    }, [modalInfo?.item, showState]);

    useEffect(() => {
        const handleChangeIllness = (e: any) => {
            if (e.detail?.bulkId && e.detail?.bulkId >= currentBulkId) { // آپدیت بالک آیدی جدید براساس دارو های گرفته شده از ویرایش
                setCurrentBulkId(e.detail?.bulkId + 1)
            }
        };

        window.addEventListener("lastBulkGroupIdForPrescription", handleChangeIllness);
        return () => window.removeEventListener("lastBulkGroupIdForPrescription", handleChangeIllness);
    }, []);

    useEffect(() => {
        if(getPrescriptionMedicationItemListRequest?.data?.data?.result) {
            const { data } = getPrescriptionMedicationItemListRequest.data.data.result,
                _data = data.map((item: any) => (
                    { label: item?.molecule, value: item?.id }
                ))
            setBulkItemsList(_data);
        }
    }, [getPrescriptionMedicationItemListRequest.data])

    const changeRequestFilters = (inputValue: any, callback: any) => {
        callbackRef.current = callback;
        setBulkItemFilterValues({
            ...bulkItemFilterValues,
            search: inputValue,
        })
    }

    useEffect(() => {
        if(callbackRef.current) {
            bulkItemsLoadOptions(bulkItemFilterValues.search, callbackRef.current);
        }
    }, [bulkItemFilterValues?.search]);

    const bulkItemsLoadOptions = (inputValue: any, callback: any) => {
        asyncSelectLoadOptions(inputValue, callback, bulkItemFilterValues, setBulkItemFilterValues, getPrescriptionMedicationItemListRequest,
            (item) => ({ label: item?.molecule, value: item?.id })
        );
    };

    const getBulkItemsIhioCodes = (bulkItems?: TIhioDataAndPrescriptionMedicationItemFields[], bulkItemFieldValues?:TIhioDataAndPrescriptionMedicationItemFields) => {
        return bulkItems
            ?.filter(item => item?.item?.id !== bulkItemFieldValues?.item?.id)
            ?.map(item => item.checkCode)
            ?.filter((code): code is string => typeof code === 'string') ?? [];
    }

    const getIhioCheckCode = async () => {
        const otherItems: string[] = getBulkItemsIhioCodes(customMedicationItem?.bulkItems, bulkItemFieldValues)

        const body = {
            id: bulkItemFieldValues?.item?.id,
            visit: modalInfo?.visitId,
            medication: true,
            itemCount: bulkItemFieldValues.itemCount,
            otherItems: otherItems,
            bulkId: modalInfo?.item?.bulkId ? modalInfo?.item?.bulkId : currentBulkId
        }
        const result = await prescriptionItemIhioCheckRequest.mutateAsync(body);
        return result?.data?.result;
    }

    const handleAddBulkToItem = async () => {
        if(bulkItemFieldValues?.itemCount && bulkItemFieldValues?.itemCount > 0 && bulkItemFieldValues?.item?.id && Number(bulkItemFieldValues?.item?.id) > 0) {
            const result = await getIhioCheckCode();
            if(bulkItemFieldValues?.UUID) {
                const updatedBulkItems = (customMedicationItem?.bulkItems ?? []).map(item =>
                    item.UUID === bulkItemFieldValues.UUID ? {...bulkItemFieldValues, ...result} : item
                )
                setCustomMedicationItem(prevState => ({
                    ...prevState,
                    bulkItems: updatedBulkItems,
                    bulkId: modalInfo?.item?.bulkId ? modalInfo?.item?.bulkId : currentBulkId,
                }))
            } else {
                const bulkItem = {
                    ...bulkItemFieldValues,
                    ...result,
                    bulkId: modalInfo?.item?.bulkId ? modalInfo?.item?.bulkId : currentBulkId,
                    UUID: uuidv4()
                }
                setCustomMedicationItem(prevState => ({
                    ...prevState,
                    bulkItems: [...(prevState?.bulkItems ?? []), bulkItem],
                    bulkId: modalInfo?.item?.bulkId ? modalInfo?.item?.bulkId : currentBulkId
                }))
            }
            setBulkItemFieldValues({})
        }
    }

    const handleRemoveBulkItem = (UUID: string) => {
        setCustomMedicationItem(prevState => ({
            ...prevState,
            bulkItems: prevState.bulkItems?.filter(item => item.UUID !== UUID) ?? []
        }));
    }

    const addPrescriptionMedicationItems = () => {
        modalInfo?.handleMedicationModalSubmit && modalInfo?.handleMedicationModalSubmit([customMedicationItem], modalInfo?.index ?? -1)
        setCurrentBulkId(modalInfo?.item?.bulkId ? currentBulkId :  currentBulkId + 1);
        handleSetShowModal();
    }

    const handleBulKItemSubmit: SubmitHandler<TIhioDataAndPrescriptionMedicationItemFields> = (data) => {
        const {id, itemCount, drugDescription} = data;
        setBulkItemFieldValues(prevState => ({
            ...prevState,
            itemCount: itemCount,
            drugDescription: drugDescription,
            item: {
                ...prevState.item,
                id: id,
                molecule: bulkItemsList?.find((i: any) => i.value === id)?.label,
            }
        }))
        handleAddBulkToItem();
    }

    const handleSetShowModal = () => {
        setBulkItemFieldValues({})
        setModalInfo({})
        setCustomMedicationItem({})
        reset({});
        setShowState(false);
        setModalInfo({});
        handleHideModal();
    }

    return (
        <Modal
            setShow={handleSetShowModal}
            showState={showState}
            noCloseBtn={true}
            className="w-1000 mw-100"
            bodyClassName="py-5 px-3 px-lg-5 pos-rel"
        >
            <div className='overflow-y-auto overflow-x-hidden'>
                <div className='d-flex mb-4 fs-3 px-2'>
                    <div className=" px-2 flex-fill align-content-center">
                        <h4 className="d-flex align-items-center title-dot font-16 font-weight-bold">
                            افزودن داروی ترکیبی
                        </h4>
                    </div>
                    <span className='text-red-hover align-content-center mt-2' onClick={() => handleSetShowModal()}>
                      <Icon name='close-square'/>
                    </span>
                </div>
                <form onSubmit={handleSubmit(handleBulKItemSubmit)} className={'mb-3'}>
                    <div className={'d-flex align-items-center w-100'}>
                        <div className="col-12 col-md-6 px-4">
                            <div className="form-group mb-3">
                                <label className="inp-lbl me-2 mb-2" htmlFor="id">نام داروی ترکیبی</label>
                                <AsyncSelectField
                                    options={bulkItemsList}
                                    name="id"
                                    placeholder='انتخاب کنید'
                                    control={control}
                                    error={errors}
                                    asyncLoadOptions={changeRequestFilters}
                                />
                            </div>
                        </div>
                        <div className="col-12 col-md-6 px-4">
                            <div className="form-group mb-3">
                                <label className="inp-lbl me-2 mb-2" htmlFor="itemCount">مقدار</label>
                                <NumberField
                                    name="itemCount"
                                    placeholder="مقدار"
                                    control={control}
                                    error={errors}
                                    isNumericString
                                    maxLength={3}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="col-12 px-4 mt-2">
                        <div className="form-group mb-2">
                            <label className="inp-lbl me-2 mb-2" htmlFor="drugDescription">توضیحات</label>
                            <TextAreaField
                                name="drugDescription"
                                rows={2}
                                placeholder="توضیحات"
                                control={control}
                                error={errors}
                            />
                        </div>
                    </div>
                    <div className={'w-100 mt-3 px-4'}>
                        <button
                            // onClick={handleAddBulkToItem}
                            type={'submit'}
                            className="btn btn-primary flex-grow-1 py-2 mx-1 w-100"
                        >
                            {bulkItemFieldValues?.UUID ? 'ویرایش ترکیب' : `افزودن به ترکیب`}
                        </button>
                    </div>
                </form>
                <div className={'w-100 px-4'}>
                    {customMedicationItem?.bulkItems && customMedicationItem?.bulkItems?.length > 0 ? (
                        <>
                            {customMedicationItem?.bulkItems?.map((item) => {
                                return (
                                    <BulkItemCardItem
                                        key={item.UUID}
                                        bulkItemFieldValues={bulkItemFieldValues}
                                        bulkItem={item}
                                        handleRemoveBulkItem={handleRemoveBulkItem}
                                        setBulkItemFieldValues={setBulkItemFieldValues}
                                    />
                                )
                            })}
                        </>
                    ) : (
                        <div
                            className={'card flex-row align-items-center justify-content-center border-2 border-dashed rounded-4 w-100 mt-5 py-5 px-5'}>
                            <span className={'text-gray-800 ms-3'}>
                                ترکیبی یافت نشد!
                            </span>
                        </div>
                    )}
                </div>
            </div>

            <div className="d-flex justify-content-center mt-4 pt-2">
                <button
                    onClick={() => handleSetShowModal()}
                    className="btn btn-outline-gray flex-grow-1 py-2 mx-1"
                >
                    انصراف
                </button>
                <button
                    onClick={addPrescriptionMedicationItems}
                    className="btn btn-primary flex-grow-1 py-2 mx-1"
                >
                    افزودن داروی ترکیبی
                </button>
            </div>
        </Modal>
    );
};

const mapStateToProps = (state: any) => ({
    modalData: modalDataSelector(state),
    modalName: modalNameSelector(state),
    isShowModalData: isShowModalDataSelector(state),
    isHideModalData: isHideModalDataSelector(state),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    handleHideModal: () => dispatch(general.handleHideModal()),
    handleResetModal: () => dispatch(general.handleResetModal()),
});

export default connect(mapStateToProps, mapDispatchToProps)(AddPrescriptionBulkItemModal);
